import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  Users, 
  MessageSquare, 
  Activity,
  Calendar,
  BarChart3,
  PieChart,
  RefreshCw,
  UserCheck,
  Eye,
  Download,
  Clock,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { adminApi } from '../../../services/admin/adminApi';
import type { 
  AnalyticsResponse, 
  UsageTrendsResponse, 
  UserDistribution, 
  TopUsersResponse,
  RealtimeStatsResponse 
} from '../../../services/admin/types';

interface AnalyticsState {
  overview: AnalyticsResponse | null;
  trends: UsageTrendsResponse | null;
  distribution: UserDistribution | null;
  topUsers: TopUsersResponse | null;
  realtimeStats: RealtimeStatsResponse | null;
  loading: boolean;
  error: string | null;
  selectedPeriod: string;
  lastUpdated: Date | null;
}

export default function AdminAnalytics() {
  const [state, setState] = useState<AnalyticsState>({
    overview: null,
    trends: null,
    distribution: null,
    topUsers: null,
    realtimeStats: null,
    loading: true,
    error: null,
    selectedPeriod: '30d',
    lastUpdated: null
  });

  // Load all analytics data
  const loadAnalytics = async (period: string = state.selectedPeriod) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const [overview, trends, distribution, topUsers, realtimeStats] = await Promise.all([
        adminApi.getAnalyticsOverview(period),
        adminApi.getUsageTrends(period),
        adminApi.getUserDistribution(),
        adminApi.getTopUsers(10, 'messages'),
        adminApi.getRealtimeStats()
      ]);

      setState(prev => ({
        ...prev,
        overview,
        trends,
        distribution,
        topUsers,
        realtimeStats,
        loading: false,
        lastUpdated: new Date()
      }));
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        error: error.response?.data?.detail || 'Failed to load analytics data',
        loading: false
      }));
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadAnalytics();
    
    // Set up auto-refresh every 5 minutes
    const interval = setInterval(() => {
      loadAnalytics();
    }, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Handle period change
  const handlePeriodChange = (period: string) => {
    setState(prev => ({ ...prev, selectedPeriod: period }));
    loadAnalytics(period);
  };

  // Format numbers for display
  const formatNumber = (num: number): string => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  // Calculate percentage change indicator
  const getGrowthIndicator = (rate: number) => {
    if (rate > 0) {
      return (
        <div className="flex items-center text-green-600">
          <ArrowUp className="w-4 h-4 mr-1" />
          <span className="text-sm font-medium">+{rate}%</span>
        </div>
      );
    } else if (rate < 0) {
      return (
        <div className="flex items-center text-red-600">
          <ArrowDown className="w-4 h-4 mr-1" />
          <span className="text-sm font-medium">{rate}%</span>
        </div>
      );
    }
    return (
      <div className="flex items-center text-gray-500">
        <span className="text-sm font-medium">0%</span>
      </div>
    );
  };

  if (state.loading && !state.overview) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-600">Real-time insights into your platform usage and performance</p>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Period Selector */}
          <select
            value={state.selectedPeriod}
            onChange={(e) => handlePeriodChange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
          
          {/* Refresh Button */}
          <button
            onClick={() => loadAnalytics()}
            disabled={state.loading}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${state.loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {state.error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{state.error}</p>
        </div>
      )}

      {/* Overview Cards */}
      {state.overview && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Users</p>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(state.overview.overview.total_users)}</p>
              </div>
              <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            {getGrowthIndicator(state.overview.overview.growth_rate)}
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Users</p>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(state.overview.overview.active_users)}</p>
              </div>
              <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Activity className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="text-sm text-gray-500 mt-2">
              {state.selectedPeriod === '7d' ? 'Last 7 days' : state.selectedPeriod === '30d' ? 'Last 30 days' : 'Last 90 days'}
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Messages</p>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(state.overview.overview.total_messages)}</p>
              </div>
              <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <MessageSquare className="h-6 w-6 text-purple-600" />
              </div>
            </div>
            <div className="text-sm text-gray-500 mt-2">This month</div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avg Messages/User</p>
                <p className="text-2xl font-bold text-gray-900">{state.overview.overview.avg_messages_per_user}</p>
              </div>
              <div className="h-12 w-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-orange-600" />
              </div>
            </div>
            <div className="text-sm text-gray-500 mt-2">This month</div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Website Contacts</p>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(state.overview.overview.unique_visitors || 0)}</p>
              </div>
              <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Users className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="text-sm text-gray-500 mt-2">Visitors who chatted</div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Team Members</p>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(state.overview.overview.team_members || 0)}</p>
              </div>
              <div className="h-12 w-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                <UserCheck className="h-6 w-6 text-indigo-600" />
              </div>
            </div>
            <div className="text-sm text-gray-500 mt-2">Across all orgs</div>
          </div>
        </div>
      )}

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Growth Chart Placeholder */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">User Growth</h3>
            <BarChart3 className="w-5 h-5 text-gray-400" />
          </div>
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
            <p className="text-gray-500">Chart component will be implemented here</p>
          </div>
        </div>

        {/* User Distribution Chart Placeholder */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">User Distribution</h3>
            <PieChart className="w-5 h-5 text-gray-400" />
          </div>
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
            <p className="text-gray-500">Pie chart component will be implemented here</p>
          </div>
        </div>
      </div>

      {/* Top Users */}
      {state.topUsers && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Top Users by Messages</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Messages</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {state.topUsers.top_users.map((user, index) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-600">#{index + 1}</span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {user.user_type.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.subscription_plan}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatNumber(user.metric_value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Last Updated */}
      {state.lastUpdated && (
        <div className="text-center text-sm text-gray-500">
          Last updated: {state.lastUpdated.toLocaleString()}
        </div>
      )}
    </div>
  );
}